import React, { useCallback, useState, useRef } from 'react';
import { initializeRazorpay } from '../services/paymentService';
import { verifyPayment, validateReferralCode, validateScholarshipCode, verifyScholarshipOTP } from '../services/api';
import CouponInput from './CouponInput'; // Import CouponInput component
import Alert from '../common/Alert'; // Import Alert component
const API_URL = process.env.REACT_APP_API_URL || 'https://register.educatory.ac/api';
// const API_URL = 'https://register.educatory.ac/api';

const PaymentGateway = ({ formData, onPaymentSuccess, backToBasicForm }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [appliedCoupons, setAppliedCoupons] = useState([]);
  const [alert, setAlert] = useState(null);
  const [referralCode, setReferralCode] = useState('');
  const [referralStatus, setReferralStatus] = useState(null);
  const [referralAlert, setReferralAlert] = useState(null);
  const [referralLoading, setReferralLoading] = useState(false);
  const [discountType, setDiscountType] = useState('none'); // 'none', 'referral', 'coupon', 'scholarship'

  const [scholarshipCode, setScholarshipCode] = useState('');
  const [scholarshipStatus, setScholarshipStatus] = useState(null);
  const [scholarshipOTP, setScholarshipOTP] = useState('');
  const [showScholarshipOTP, setShowScholarshipOTP] = useState(false);
  const [scholarshipLoading, setScholarshipLoading] = useState(false);

  const baseCourseFee = formData.courseFee / 1.18; // Base fee before GST

  // Validate and adjust coupon discounts
  const validateCouponDiscounts = (coupons) => {
    if (!coupons || coupons.length === 0) return coupons;

    const maxAllowedDiscount = formData.courseFee - 1;
    let totalDiscount = 0;
    const validatedCoupons = [];

    for (const coupon of coupons) {
      const couponDiscount = parseFloat(parseFloat(coupon.amount).toFixed(2));

      if (totalDiscount + couponDiscount <= maxAllowedDiscount) {
        // Coupon can be applied as is
        validatedCoupons.push(coupon);
        totalDiscount += couponDiscount;
      } else {
        // Adjust coupon discount to fit within limit
        const adjustedDiscount = parseFloat((maxAllowedDiscount - totalDiscount).toFixed(2));
        if (adjustedDiscount > 0) {
          validatedCoupons.push({
            ...coupon,
            amount: adjustedDiscount,
            adjusted: true
          });
          totalDiscount += adjustedDiscount;
        }
        break; // No more coupons can be applied
      }
    }

    return validatedCoupons;
  };

  // Handle discount type change
  const handleDiscountTypeChange = (type) => {
    setDiscountType(type);

    // Reset states when changing discount type
    if (type !== 'referral') {
      setReferralCode('');
      setReferralStatus(null);
      setReferralAlert(null);
    }

    if (type !== 'coupon') {
      setAppliedCoupons([]);
    }

    if (type !== 'scholarship') {
      setScholarshipCode('');
      setScholarshipStatus(null);
      setScholarshipOTP('');
      setShowScholarshipOTP(false);
    }
  };

  // Referral validation logic
  const handleReferralValidation = async (e) => {
    const code = e.target.value.toUpperCase();
    setReferralCode(code);
    setReferralStatus(null);
    setReferralAlert(null);

    if (code.length === 6) {
      setReferralLoading(true);
      try {
        const response = await validateReferralCode({
          referralCode: code,
          email: formData.email,
          phone: formData.phone,
          aadharNumber: formData.aadharNumber,
          courseName: formData.courseName
        });

        if (response.success) {
          setReferralStatus({
            isValid: true,
            message: `Valid referral from ${response.data.referrerName}. You'll get ₹${response.data.discountPerClass} off on this course!`
          });
        }
      } catch (error) {
        const errorMessage = error.response?.data?.warningMessage || error.response?.data?.message || 'Invalid referral code';
        setReferralStatus({
          isValid: false,
          message: errorMessage
        });

        setReferralAlert({
          type: 'error',
          message: errorMessage
        });

        setReferralCode('');
      } finally {
        setReferralLoading(false);
      }
    }
  };

  // Scholarship validation logic
  const handleScholarshipValidation = async () => {
    if (!scholarshipCode || scholarshipCode.length < 10) return;

    setScholarshipLoading(true);
    setScholarshipStatus(null);
    try {
      const response = await validateScholarshipCode({
        scholarshipCode,
        courseAmount: formData.courseFee
      });
      if (response.success) {
        setScholarshipStatus({ isValid: true, message: response.message, discountAmount: response.discountAmount, is100Percent: response.is100Percent });
        setShowScholarshipOTP(true);
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Invalid scholarship code';
      setScholarshipStatus({ isValid: false, message: errorMessage });
      setAlert({ type: 'error', message: errorMessage });
    } finally {
      setScholarshipLoading(false);
    }
  };

  const handleScholarshipOTPVerify = async () => {
    if (!scholarshipOTP || scholarshipOTP.length !== 6) return;

    setScholarshipLoading(true);
    try {
      const response = await verifyScholarshipOTP({
        otp: scholarshipOTP,
        scholarshipCode,
        enrollmentId: localStorage.getItem('enrollmentId')
      });
      if (response.success) {
        setScholarshipStatus({
          isValid: true,
          verified: true,
          percentage: response.percentage,
          is100Percent: response.is100Percent,
          message: `Scholarship verified successfully! ${response.percentage}% discount applied.`
        });
        setShowScholarshipOTP(false);
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Invalid OTP';
      setAlert({ type: 'error', message: errorMessage });
    } finally {
      setScholarshipLoading(false);
    }
  };

  // Updated discount calculation to use referralCode state
  const calculateReferralDiscount = () => {
    if (discountType !== 'referral' || !referralCode || !referralStatus?.isValid) return 0;
    const baseDiscount = parseFloat((baseCourseFee * 0.1).toFixed(2));
    return parseFloat((baseDiscount * 1.18).toFixed(2));
  };

  const calculateCouponDiscount = () => {
    if (discountType !== 'coupon') return 0;

    const totalCouponDiscount = appliedCoupons.reduce((sum, coupon) => {
      const baseDiscount = parseFloat(coupon.amount);
      return sum + baseDiscount;
    }, 0);

    // Ensure total coupon discount doesn't exceed (total fee - 1)
    const maxAllowedDiscount = formData.courseFee - 1;
    return parseFloat(Math.min(totalCouponDiscount, maxAllowedDiscount).toFixed(2));
  };

  const calculateScholarshipDiscount = () => {
    if (discountType !== 'scholarship' || !scholarshipStatus?.verified || !scholarshipStatus?.percentage) return 0;
    if (scholarshipStatus.is100Percent) {
      return formData.courseFee; // Full course fee is discounted
    }
    const baseDiscount = parseFloat((baseCourseFee * (scholarshipStatus.percentage / 100)).toFixed(2));
    return parseFloat((baseDiscount * 1.18).toFixed(2));
  };

  const calculateFinalAmount = () => {
    const referralDiscount = calculateReferralDiscount();
    const couponDiscount = calculateCouponDiscount();
    const scholarshipDiscount = calculateScholarshipDiscount();

    const totalDiscounts = referralDiscount + couponDiscount + scholarshipDiscount;

    // 100% scholarship: charge only the platform fee
    if (scholarshipStatus?.verified && scholarshipStatus?.is100Percent) {
      return 118; // ₹100 + 18% GST platform fee
    }

    // Ensure final amount is at least 1
    const finalAmount = Math.max(1, formData.courseFee - totalDiscounts);
    return parseFloat(finalAmount.toFixed(2));
  };

  const handlePayment = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const isLoaded = await initializeRazorpay();
      if (!isLoaded) {
        throw new Error('Razorpay SDK failed to load');
      }

      const finalAmount = calculateFinalAmount();

      // Create an order on your backend
      const response = await fetch(API_URL + '/payments/create-order', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          amount: Math.round(finalAmount * 100),
          currency: 'INR',
          receipt: `receipt_${Date.now()}`,
          notes: {
            studentName: `${formData.firstName} ${formData.lastName}`,
            email: formData.email,
            phone: formData.phone,
            enrollmentId: localStorage.getItem('enrollmentId'),
            referralCode: discountType === 'referral' ? referralCode : '',
            appliedCoupons: discountType === 'coupon' ? appliedCoupons.map(c => c.code).join(',') : '',
            scholarshipCode: discountType === 'scholarship' && scholarshipStatus?.verified ? scholarshipCode : ''
          },
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create order');
      }

      const orderData = await response.json();

      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: parseFloat(orderData.amount),
        currency: orderData.currency,
        name: 'Course Registration',
        description: `Fee for ${formData.courseName}`,
        order_id: orderData.id,
        handler: async function (response) {
          try {
            // Verify payment on the backend
            const verificationResponse = await verifyPayment({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              enrollmentId: localStorage.getItem('enrollmentId'),
              appliedCoupons: discountType === 'coupon' ? appliedCoupons : [],
              referralCode: discountType === 'referral' ? referralCode : '',
              scholarshipCode: discountType === 'scholarship' && scholarshipStatus?.verified ? scholarshipCode : ''
            });

            if (verificationResponse.success) {
              onPaymentSuccess(response);
              document.body.style.overflow = 'auto';
            } else {
              setAlert({
                type: 'error',
                message: 'Payment verification failed. Please contact support.'
              });
            }
          } catch (error) {
            console.error('Payment verification failed:', error);
            setAlert({
              type: 'error',
              message: 'Payment verification failed. Please try again.'
            });
          }
        },
        prefill: {
          name: `${formData.firstName} ${formData.lastName}`,
          email: formData.email,
          contact: formData.phone,
        },
        theme: {
          color: '#3399cc',
        },
      };
      const paymentObject = new window.Razorpay(options);
      paymentObject.open();
    } catch (error) {
      console.error('Payment initialization failed:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Payment gateway failed to load';
      setError(errorMessage);
      setAlert({
        type: 'error',
        message: errorMessage
      });
    } finally {
      setIsLoading(false);
    }
  }, [formData, onPaymentSuccess, appliedCoupons, referralCode, referralStatus, discountType, scholarshipStatus, scholarshipCode]);

  if (isLoading) {
    return (
      <div className="payment-gateway">
        <h2>Secure Payment Processing</h2>
        <p className="payment-subtitle">Please wait while we set up your payment...</p>
        <div className="payment-loading">
          <div className="spinner"></div>
          <p>Preparing a secure payment environment for you</p>
        </div>
      </div>
    );
  }

  const rawTotalDiscounts = calculateReferralDiscount() + calculateCouponDiscount() + calculateScholarshipDiscount();
  const maxAllowedDiscount = formData.courseFee - 1;
  const effectiveTotalDiscounts = Math.min(rawTotalDiscounts, maxAllowedDiscount);

  const discountBase = parseFloat((effectiveTotalDiscounts / 1.18).toFixed(2));
  const discountGst = parseFloat((effectiveTotalDiscounts - discountBase).toFixed(2));

  const originalBase = baseCourseFee;
  const originalGst = parseFloat((baseCourseFee * 0.18).toFixed(2));

  const newBase = Math.max(0, originalBase - discountBase);
  const newGst = Math.max(0, originalGst - discountGst);

  return (
    <div className="payment-gateway">
      {alert && (
        <Alert
          message={alert.message}
          type={alert.type}
          onClose={() => setAlert(null)}
        />
      )}
      {referralAlert && (
        <Alert
          message={referralAlert.message}
          type={referralAlert.type}
          onClose={() => setReferralAlert(null)}
        />
      )}
      <div style={{ display: 'flex' }}>
        <button className='back-button' onClick={() => backToBasicForm()}>⬅️ Edit Basic Details</button>
      </div>
      <h2>Complete Your Registration</h2>
      <div className="payment-details">
        {/* COURSE AND AMOUNT DETAILS */}
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '1rem', marginBottom: 1 }}>
          <span style={{ fontWeight: 500, textAlign: 'left' }}>Course Name: </span>
          <span style={{ fontWeight: 500, textAlign: 'right' }}>{formData.courseName}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '1rem', marginBottom: 15 }}>
          <span style={{ textAlign: 'left' }}>Course ID</span>
          <span style={{ textAlign: 'right' }}>{formData.courseId}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '1rem', marginBottom: 4 }}>
          <span style={{ textAlign: 'left' }}>Base Fee</span>
          <span style={{ textAlign: 'right' }}>₹{baseCourseFee.toFixed(2)}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '1rem', marginBottom: 4 }}>
          <span style={{ textAlign: 'left' }}>GST (18%)</span>
          <span style={{ textAlign: 'right' }}>₹{(formData.courseFee - baseCourseFee).toFixed(2)}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '1rem', marginBottom: 12 }}>
          <span style={{ textAlign: 'left', fontWeight: 600 }}>Total Amount</span>
          <span style={{ textAlign: 'right', fontWeight: 600 }}>₹{formData.courseFee.toFixed(2)}</span>
        </div>

        {/* DISCOUNT SELECTION */}
        <div className="discount-selection" style={{ marginBottom: 20 }}>
          <h3 style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: 12, textAlign: 'left' }}>Do you have a Referral/Coupon Code ?</h3>

          {/* Radio Button Options */}
          <div className="discount-options" style={{ marginBottom: 16, textAlign: 'left' }}>
            <div style={{ marginBottom: 8 }}>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="discountType"
                  value="none"
                  checked={discountType === 'none'}
                  onChange={() => handleDiscountTypeChange('none')}
                  style={{ marginRight: 8 }}
                />
                <span>No</span>
              </label>
            </div>

            <div style={{ marginBottom: 8 }}>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="discountType"
                  value="referral"
                  checked={discountType === 'referral'}
                  onChange={() => handleDiscountTypeChange('referral')}
                  style={{ marginRight: 8 }}
                />
                <span>Referral Code (For new students enrolling for first time)</span>
              </label>
            </div>

            <div style={{ marginBottom: 8 }}>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="discountType"
                  value="coupon"
                  checked={discountType === 'coupon'}
                  onChange={() => handleDiscountTypeChange('coupon')}
                  style={{ marginRight: 8 }}
                />
                <span>Coupon Code (For existing students who are already enrolled in one or more courses)</span>
              </label>
            </div>

            <div style={{ marginBottom: 8 }}>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="discountType"
                  value="scholarship"
                  checked={discountType === 'scholarship'}
                  onChange={() => handleDiscountTypeChange('scholarship')}
                  style={{ marginRight: 8 }}
                />
                <span>Scholarship Code (For students with an approved scholarship)</span>
              </label>
            </div>
          </div>

          {/* Eligibility Information */}
          {/* <div className="eligibility-info" style={{ 
            backgroundColor: '#f8f9fa', 
            padding: '12px', 
            borderRadius: '6px', 
            fontSize: '0.9rem',
            color: '#666',
            marginBottom: 16
          }}>
            <h4 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: 8, color: '#333' }}>
              📋 Eligibility Information
            </h4>
            <div style={{ marginBottom: 8 }}>
              <strong>🔗 Referral Code:</strong> For new students enrolling for first time
            </div>
            <div>
              <strong>🎟️ Coupon Code:</strong> For existing students who are already enrolled in one or more courses
            </div>
          </div> */}
        </div>

        {/* REFERRAL INPUT SECTION */}
        {discountType === 'referral' && (
          <div className="referral-section" style={{ marginBottom: 16 }}>
            <div className="form-group">
              <label htmlFor="referralCode" style={{ fontWeight: 500, textAlign: 'left', display: 'block', marginBottom: 8 }}>
                Enter Referral Code
              </label>
              <input
                type="text"
                id="referralCode"
                name="referralCode"
                value={referralCode}
                onChange={handleReferralValidation}
                placeholder="Enter 6-digit referral code"
                pattern="[A-Z0-9]{6}"
                maxLength={6}
                style={{ fontSize: '1rem', width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
              />
              {referralLoading && <span style={{ fontSize: '0.9rem', color: '#888', marginTop: 4, display: 'block' }}>Validating...</span>}
              {referralStatus && (
                <p className={`referral-status ${referralStatus.isValid ? 'success' : 'error'}`} style={{
                  fontSize: '0.95rem',
                  marginTop: 8,
                  color: referralStatus.isValid ? '#28a745' : '#dc3545'
                }}>
                  {referralStatus.message}
                </p>
              )}
            </div>
          </div>
        )}

        {/* COUPON INPUT SECTION */}
        {discountType === 'coupon' && (
          <div className="coupon-section" style={{ marginBottom: 16 }}>
            <CouponInput
              courseFee={formData.courseFee}
              onCouponApplied={(coupons) => {
                const validatedCoupons = validateCouponDiscounts(coupons);
                setAppliedCoupons(validatedCoupons);
              }}
              appliedCoupons={appliedCoupons}
              canApplyCoupon={true}
              maxDiscount={formData.courseFee - 1}
            />
            {appliedCoupons.some(coupon => coupon.adjusted) && (
              <p style={{ fontSize: '0.9rem', color: '#ff6b35', marginTop: 8 }}>
                ⚠️ Some coupon discounts have been adjusted to ensure the final amount remains at least ₹1
              </p>
            )}
          </div>
        )}

        {/* SCHOLARSHIP INPUT SECTION */}
        {discountType === 'scholarship' && (
          <div className="scholarship-section" style={{ marginBottom: 16 }}>
            <div className="form-group">
              <label htmlFor="scholarshipCode" style={{ fontWeight: 500, textAlign: 'left', display: 'block', marginBottom: 8 }}>
                Enter Scholarship Code
              </label>
              <div style={{ display: 'flex', gap: '10px' }}>
                <input
                  type="text"
                  id="scholarshipCode"
                  name="scholarshipCode"
                  value={scholarshipCode}
                  onChange={(e) => setScholarshipCode(e.target.value.toUpperCase())}
                  placeholder="e.g., EDSCH12345"
                  disabled={scholarshipStatus?.verified || showScholarshipOTP}
                  style={{ fontSize: '1rem', width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                />
                {!scholarshipStatus?.verified && !showScholarshipOTP && (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleScholarshipValidation}
                    disabled={scholarshipLoading || !scholarshipCode}
                  >
                    {scholarshipLoading ? '...' : 'Verify'}
                  </button>
                )}
              </div>

              {showScholarshipOTP && (
                <div style={{ marginTop: '10px', display: 'flex', gap: '10px' }}>
                  <input
                    type="text"
                    value={scholarshipOTP}
                    onChange={(e) => setScholarshipOTP(e.target.value)}
                    placeholder="Enter 6-digit OTP from email"
                    maxLength={6}
                    style={{ fontSize: '1rem', width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                  />
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleScholarshipOTPVerify}
                    disabled={scholarshipLoading || scholarshipOTP.length !== 6}
                  >
                    {scholarshipLoading ? '...' : 'Submit OTP'}
                  </button>
                </div>
              )}

              {scholarshipStatus && (
                <p className={`referral-status ${scholarshipStatus.isValid ? 'success' : 'error'}`} style={{
                  fontSize: '0.95rem',
                  marginTop: 8,
                  color: scholarshipStatus.isValid ? '#28a745' : '#dc3545'
                }}>
                  {scholarshipStatus.message}
                </p>
              )}
            </div>
          </div>
        )}

        {/* DISCOUNT ROWS - SHOWN ONLY IF DISCOUNTS ARE APPLIED */}
        {scholarshipStatus?.verified && scholarshipStatus?.is100Percent ? (
          <div className="discount-section" style={{
            marginBottom: 16,
            padding: '16px',
            background: 'linear-gradient(to right bottom, #ffffff, #f8fafc)',
            borderRadius: '12px',
            border: '1px solid #e2e8f0',
            boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
          }}>
            {/* Show full course fee struck through → ₹0 */}
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '1.05rem', color: '#475569', marginBottom: '12px' }}>
              <span style={{ textAlign: 'left' }}>Course Fee</span>
              <span><s style={{ color: '#94a3b8' }}>₹{formData.courseFee.toFixed(2)}</s> &rarr; <span style={{ fontWeight: 'bold', color: '#059669' }}>₹0.00</span></span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
              <span style={{ color: '#16a34a', fontWeight: '600', backgroundColor: '#ecfdf5', padding: '6px 12px', borderRadius: '8px', border: '1px solid #d1fae5' }}>
                🎓 100% Scholarship Applied!
              </span>
            </div>

            {/* Platform fee breakdown */}
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '1.05rem', color: '#0f172a', borderTop: '1px solid #e2e8f0', paddingTop: '12px', marginBottom: '8px' }}>
              <span style={{ textAlign: 'left' }}>Platform Fee</span>
              <span style={{ fontWeight: 500 }}>₹100.00</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '1.05rem', color: '#0f172a', marginBottom: '12px' }}>
              <span style={{ textAlign: 'left' }}>GST on Platform Fee (18%)</span>
              <span style={{ fontWeight: 500 }}>₹18.00</span>
            </div>

            {/* Final amount */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              fontSize: '1.25rem',
              fontWeight: 700,
              marginTop: 14,
              borderTop: '2px dashed #cbd5e1',
              paddingTop: 12,
              color: '#16a34a'
            }}>
              <span style={{ textAlign: 'left' }}>Final Amount</span>
              <span style={{ textAlign: 'right' }}>₹118.00</span>
            </div>
          </div>
        ) : effectiveTotalDiscounts > 0 ? (
          <div className="discount-section" style={{
            marginBottom: 16,
            padding: '16px',
            background: 'linear-gradient(to right bottom, #ffffff, #f8fafc)',
            borderRadius: '12px',
            border: '1px solid #e2e8f0',
            boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '1.1rem', fontWeight: 600, color: '#0f172a', marginBottom: '12px' }}>
              <span style={{ textAlign: 'left' }}>Net Discount</span>
              <span style={{ color: '#dc2626', textAlign: 'right' }}>-₹{effectiveTotalDiscounts.toFixed(2)}</span>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.95rem', color: '#475569', marginBottom: '16px' }}>
              <span style={{ textAlign: 'left' }}>
                <strong>Net Discount on Course Fee</strong> <br />
                <div style={{ fontSize: '1rem', marginTop: '6px', backgroundColor: '#ecfdf5', display: 'inline-block', padding: '4px 10px', borderRadius: '6px', border: '1px solid #d1fae5' }}>
                  <span style={{ textDecoration: 'line-through', color: '#94a3b8', fontSize: '0.9rem' }}>₹{originalBase.toFixed(2)}</span>
                  <span style={{ margin: '0 8px', color: '#10b981' }}>→</span>
                  <span style={{ color: '#059669', fontWeight: 'bold', fontSize: '1.05rem' }}>₹{newBase.toFixed(2)}</span>
                </div>
              </span>
              <span style={{ color: '#ef4444', textAlign: 'right', fontWeight: 600, fontSize: '1.05rem', alignSelf: 'center' }}>-₹{discountBase.toFixed(2)}</span>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.95rem', color: '#475569', marginBottom: '8px' }}>
              <span style={{ textAlign: 'left' }}>
                <strong>Net Discount on GST</strong> <br />
                <div style={{ fontSize: '1rem', marginTop: '6px', backgroundColor: '#ecfdf5', display: 'inline-block', padding: '4px 10px', borderRadius: '6px', border: '1px solid #d1fae5' }}>
                  <span style={{ textDecoration: 'line-through', color: '#94a3b8', fontSize: '0.9rem' }}>₹{originalGst.toFixed(2)}</span>
                  <span style={{ margin: '0 8px', color: '#10b981' }}>→</span>
                  <span style={{ color: '#059669', fontWeight: 'bold', fontSize: '1.05rem' }}>₹{newGst.toFixed(2)}</span>
                </div>
              </span>
              <span style={{ color: '#ef4444', textAlign: 'right', fontWeight: 600, fontSize: '1.05rem', alignSelf: 'center' }}>-₹{discountGst.toFixed(2)}</span>
            </div>

            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              fontSize: '1.25rem',
              fontWeight: 700,
              marginTop: 14,
              borderTop: '2px dashed #cbd5e1',
              paddingTop: 12,
              color: '#16a34a'
            }}>
              <span style={{ textAlign: 'left' }}>Final Amount</span>
              <span style={{ textAlign: 'right' }}>₹{calculateFinalAmount().toFixed(2)}</span>
            </div>
          </div>
        ) : null}
      </div>

      {error ? (
        <div className="error-container">
          <p className="error-message">
            <span className="error-icon">⚠️</span>
            {error}
          </p>
          <p className="error-help">Don't worry! No amount has been charged.</p>
        </div>
      ) : (
        <p className="security-note">
          <span className="lock-icon">🔒</span>
          Your payment is secured with industry-standard encryption
        </p>
      )}
      <button
        onClick={handlePayment}
        className="btn btn-primary payment-btn"
        disabled={isLoading}
      >
        {isLoading ? (
          <>
            <span className="spinner-small"></span>
            Processing...
          </>
        ) : (
          <>
            <span className="pay-icon">💳</span>
            Proceed to Payment
          </>
        )}
      </button>
      {!error && (
        <p className="payment-help">
          Need help? Contact us at <a href="mailto:contact@educatory.ac">contact@educatory.ac</a>
        </p>
      )}
    </div>
  );
};

export default PaymentGateway;